import React, { useEffect } from "react";
import Header from '../../../components/common/Header/Header';
import { AppDispatch } from '../../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchRole, fetchRoleUsers } from '../../../redux/actions/roles.actions';
import RootState from '../../../redux/states/root.state';
import { ShieldCheckIcon, UsersIcon } from '../../../components/common/Icons/Icons';
import { roleLabel } from '../../../utils/roles';
import PermissionsList from '../../../components/common/PermissionsList/PermissionsList';
import MembersTable from '../../../components/common/Tables/MembersTable';

type Tab = 'permissions' | 'users';

// Role Details: the shared detail shell (styles/list-page.css) — a side card about the role
// beside one card that switches between its permissions and its users.
const RoleDetailsPage = () => {
  const dispatch: AppDispatch = useDispatch();
  const {id} = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {data:role, loading:isLoading} = useSelector((state: RootState) => state.role);
  const {meta:usersMeta} = useSelector((state: RootState) => state.roleUsers);

  // Which panel is open lives in the URL so a link can open straight on the users list
  const tab: Tab = searchParams.get('tab') === 'users' ? 'users' : 'permissions';
  const page = searchParams.get('page') ?? '';

  useEffect(() => {
    if (id) dispatch(fetchRole(id));
  }, [dispatch, id]);

  // Only the page number matters to the users list (not the tab)
  useEffect(() => {
    if (id) dispatch(fetchRoleUsers({id, page: page ? `page=${page}` : ''}));
  }, [dispatch, id, page]);

  const showTab = (next: Tab) => {
    if (next === 'users') searchParams.set('tab', 'users');
    else searchParams.delete('tab');
    setSearchParams(searchParams);
  };

  const permissions = role?.permissions ?? [];
  const permissionsCount = role?.permissions_count ?? permissions.length;
  const usersCount = usersMeta?.total ?? role?.users_count ?? 0;

  return (
    <div className="page lp-page">
      <Header header="Role Details"/>
      <main className="lp-main">
        {isLoading ?
          <div className="lp-loading"><span className="spinner" role="progressbar" aria-label="Loading role"/></div>
          :
          <div className="lp-layout">
            <aside className="lp-side lp-side-fit" aria-label="Role">
              <section className="lp-side-section">
                <div className="lp-side-head">
                  <h1 className="lp-side-name">{roleLabel(role?.name)}</h1>
                </div>
                <p className="lp-side-desc">{role?.description || <span className="lp-muted">No description</span>}</p>

                <dl className="lp-facts lp-side-counts">
                  <div>
                    <dt>Permissions</dt>
                    <dd><ShieldCheckIcon/>{permissionsCount.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Users</dt>
                    <dd><UsersIcon/>{usersCount.toLocaleString()}</dd>
                  </div>
                </dl>
              </section>
            </aside>

            <div className="dt lp-panel">
              <div className="dt-toolbar">
                <div className="dt-toolbar-start">
                  <div className="segmented lp-tabs" role="tablist" aria-label="Role details">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 'permissions'}
                      className={tab === 'permissions' ? 'active' : undefined}
                      onClick={()=>showTab('permissions')}
                    >
                      <ShieldCheckIcon/> Permissions <span className="lp-tab-count">{permissionsCount.toLocaleString()}</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 'users'}
                      className={tab === 'users' ? 'active' : undefined}
                      onClick={()=>showTab('users')}
                    >
                      <UsersIcon/> Users <span className="lp-tab-count">{usersCount.toLocaleString()}</span>
                    </button>
                  </div>
                </div>
              </div>

              {tab === 'permissions'
                ? <PermissionsList permissions={permissions} emptyHint="This role doesn't grant anything on its own."/>
                : <MembersTable emptyHint="Nobody holds this role yet."/>}
            </div>
          </div>}
      </main>
    </div>
  );
};

export default RoleDetailsPage;
